import React, { useState, useEffect, useCallback } from "react";
import { useDispatch } from "react-redux";
import Select from "react-select";
import { getClients } from "../../../../services/global_redux/globale_actions/Client.action";
import { getUnits } from "../../../../services/global_redux/globale_actions/Unit.action";
import {
  getReturnsFormsnew,
  getReturnSelectedYear,
  CreateGenerateFormInput,
  uploadReturnFile,
  RETURN_MODES,
} from "../../../../services/register_redux/register_actions/ReturnsForms.action";
import { Generated_forms_downloadDataInput } from "../../../../services/register_redux/register_actions/generateForm.action";
import file_upload_icon from "../../../../assets/reUsableTemplates_assets/file_upload_icon.png";
import file_uploaded_icon from "../../../../assets/reUsableTemplates_assets/file_uploaded_icon.png";
import into from "../../../../assets/reUsableTemplates_assets/into.png";
import ClientReturnsPortal from "./ClientReturnsPortal";
import "./ReturnGenerateForm.css";

const MODES = RETURN_MODES;

const ACK_FREQUENCY_OPTIONS = [
  "Annually",
  "Semi-annually",
  "Quarterly",
];

const MODE_META = {
  [RETURN_MODES.GENERATE]: {
    title: "Generate Return",
    desc: "Create from system data",
  },
  [RETURN_MODES.OVERRIDE]: {
    title: "Override Return",
    desc: "Replace generated file",
  },
  [RETURN_MODES.ACK]: {
    title: "Acknowledgement",
    desc: "Upload acknowledgement doc",
  },
};

function unitLocation(unit) {
  return String(unit?.Location_City || unit?.location_city || "").trim();
}

function orgOptionLabel(client) {
  if (!client) return "";
  const id = String(client.client_id || "").trim();
  const name = String(client.client_name || "").trim();
  if (id && name) return `${id} - ${name}`;
  return id || name;
}

function branchOptionLabel(unit) {
  const name = String(unit?.unit_name || "").trim();
  const location = unitLocation(unit);
  const id = String(unit?.unit_id || "").trim();
  let place = "";
  if (name && location) place = `${name} (${location})`;
  else place = name || location || "";
  if (id && place) return `${id} - ${place}`;
  return id || place;
}

const selectCustomStyles = {
  control: (provided, state) => ({
    ...provided,
    fontSize: "12px",
    fontWeight: 500,
    minHeight: "42px",
    backgroundColor: "rgb(249,250,251)",
    border: state.isFocused ? "1px solid #0057ff" : "1px solid #e8ecf4",
    boxShadow: state.isFocused ? "0 0 0 3px rgba(0,87,255,0.12)" : "none",
    borderRadius: "6px",
    "&:hover": { borderColor: "#0057ff" },
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: "2px 10px",
  }),
  singleValue: (provided) => ({
    ...provided,
    fontSize: "12px",
    fontWeight: 500,
  }),
  placeholder: (provided) => ({
    ...provided,
    fontSize: "12px",
    color: "#999",
  }),
  menuList: (provided) => ({
    ...provided,
    maxHeight: "160px",
    fontSize: "12px",
  }),
  option: (provided, state) => ({
    ...provided,
    fontSize: "12px",
    backgroundColor: state.isFocused ? "#e8f0ff" : "white",
    color: "#333",
  }),
};

const YEAR_FIELD_NAMES = new Set([
  "financial_year_start",
  "year",
  "financial_year",
  "calendar_year",
  "return_year",
  "return_period",
  "fy_start",
]);

function periodYearTypeLabel(period) {
  if (period == null || String(period).trim() === "") return null;
  const p = String(period).trim().toLowerCase();
  if (p.includes("financial") || p === "fy" || p === "financial year") {
    return "Financial year";
  }
  if (p.includes("calendar") || p === "cy" || p === "calendar year") {
    return "Calendar year";
  }
  return String(period).trim();
}

function fieldInputType(field) {
  return (field?.input_type || field?.input || field?.control || "text")
    .toString()
    .toLowerCase();
}

function isPeriodYearField(field) {
  if (!field?.name) return false;
  return YEAR_FIELD_NAMES.has(field.name.toLowerCase());
}

function isUserInputField(field) {
  if (!field?.name || isPeriodYearField(field)) return false;
  const t = fieldInputType(field);
  return [
    "float",
    "number",
    "int",
    "integer",
    "text",
    "string",
    "date",
    "month",
    "textarea",
  ].includes(t);
}

function splitFieldsFromApi(data) {
  const all = Array.isArray(data?.ui_fields) ? data.ui_fields : [];
  return {
    ...data,
    form_inputs: all.filter(isUserInputField),
    period_fields: all.filter(isPeriodYearField),
  };
}

function getPeriodFieldName(generationContext) {
  return generationContext?.period_fields?.[0]?.name || "financial_year_start";
}

function buildReturnInputsPayload(dynamicValues, generationContext) {
  const out = {};
  (generationContext?.form_inputs || []).forEach((f) => {
    if (!f?.name) return;
    const raw = dynamicValues[f.name];
    if (raw == null || String(raw).trim() === "") return;
    const t = fieldInputType(f);
    if (t === "float" || t === "number" || t === "int" || t === "integer") {
      const n = parseFloat(raw);
      out[f.name] = Number.isNaN(n) ? raw : n;
    } else {
      out[f.name] = raw;
    }
  });

  const periodFieldName = getPeriodFieldName(generationContext);
  const periodValue =
    dynamicValues[periodFieldName] ?? pickDateFromDynamicValues(dynamicValues);
  if (periodValue != null && String(periodValue).trim() !== "") {
    out[periodFieldName] = String(periodValue).trim().slice(0, 10);
    if (periodFieldName !== "financial_year_start") {
      out.financial_year_start = String(periodValue).trim().slice(0, 10);
    }
  }

  return out;
}

function pickDateFromDynamicValues(dynamicValues) {
  for (const k of YEAR_FIELD_NAMES) {
    const v = dynamicValues[k];
    if (v != null && String(v).trim() !== "") return String(v).trim();
  }
  return "";
}

function applyContextDefaults(data) {
  const ctx = splitFieldsFromApi(data);
  const init = {};

  const fyDefault =
    data.default_generation_date ||
    ctx.period_fields?.[0]?.default ||
    (data.year_options?.length
      ? data.year_options[data.year_options.length - 1].value
      : "");

  if (fyDefault) {
    const periodFieldName = ctx.period_fields?.[0]?.name || "financial_year_start";
    const periodDate = String(fyDefault).slice(0, 10);
    init[periodFieldName] = periodDate;
    if (periodFieldName !== "financial_year_start") {
      init.financial_year_start = periodDate;
    }
  }

  (ctx.form_inputs || []).forEach((f) => {
    if (f.default != null && f.default !== "") {
      init[f.name] = f.default;
    }
  });

  return {
    ctx,
    init,
    processDate: fyDefault ? String(fyDefault).slice(0, 10) : "",
  };
}

function AdminReturnGenerateForm({ setLoading, showSnackbar }) {
  const [clientOptions, setClientOptions] = useState([]);
  const [clientID, setClientID] = useState("");
  const [clientName, setClientName] = useState("");
  const [returnsRows, setReturnsRows] = useState([]);
  const [selectedUnit, setSelectedUnit] = useState("");
  const [selectedForm, setSelectedForm] = useState("");
  const [selectedAckFrequency, setSelectedAckFrequency] = useState("");
  const [selectedAckYear, setSelectedAckYear] = useState("");
  const [selectedMode, setSelectedMode] = useState(MODES.GENERATE);
  const [processDate, setProcessDate] = useState("");
  const [selectedFormat, setSelectedFormat] = useState("excel");
  const [successMessage, setSuccessMessage] = useState("");
  const [failMessage, setFailMessage] = useState("");
  const [unitOptions, setUnitOptions] = useState([]);
  const [generationContext, setGenerationContext] = useState(null);
  const [dynamicValues, setDynamicValues] = useState({});
  const [file, setFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [storedFormId, setStoredFormId] = useState(null);
  const dispatch = useDispatch();

  const yearTypeLabel = generationContext?.period
    ? periodYearTypeLabel(generationContext.period)
    : null;

  const hasYearOptions =
    Array.isArray(generationContext?.year_options) &&
    generationContext.year_options.length > 0;

  const formInputs = generationContext?.form_inputs || [];

  const toMonthFirstDay = useCallback((isoDateStr) => {
    if (!isoDateStr) return "";
    const d = new Date(isoDateStr);
    if (Number.isNaN(d.getTime())) return isoDateStr;
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    return `${y}-${m}-01`;
  }, []);

  const resetMessages = () => {
    setSuccessMessage("");
    setFailMessage("");
  };

  const resetStoredForm = () => setStoredFormId(null);

  const resetFile = () => setFile(null);

  const handleModeChange = (e) => {
    setSelectedMode(e.target.value);
    setSelectedForm("");
    setSelectedAckFrequency("");
    setSelectedAckYear("");
    setReturnsRows([]);
    setProcessDate("");
    setGenerationContext(null);
    setDynamicValues({});
    resetMessages();
    resetFile();
    resetStoredForm();
  };

  const renderUserInput = (field) => {
    const t = fieldInputType(field);
    const value = dynamicValues[field.name] ?? "";
    const onChange = (val) =>
      setDynamicValues((prev) => ({ ...prev, [field.name]: val }));

    if (t === "float" || t === "number" || t === "int" || t === "integer") {
      return (
        <input
          className="form-control"
          type="number"
          step="any"
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
      );
    }
    if (t === "textarea") {
      return (
        <textarea
          className="form-control"
          rows={2}
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
      );
    }
    if (t === "date") {
      return (
        <input
          className="form-control"
          type="date"
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
      );
    }
    if (t === "month") {
      return (
        <input
          className="form-control"
          type="month"
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
      );
    }
    return (
      <input
        className="form-control"
        type="text"
        value={value}
        placeholder={field.placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await dispatch(getClients());
        if (cancelled) return;
        if (res.success && Array.isArray(res.data)) {
          setClientOptions(res.data);
        } else {
          setClientOptions([]);
          showSnackbar(res.message || "Could not load organizations.", "error");
        }
      } catch {
        if (!cancelled) {
          setClientOptions([]);
          showSnackbar("Could not load organizations.", "error");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  useEffect(() => {
    if (selectedMode === MODES.ACK) {
      setGenerationContext(null);
      setDynamicValues({});
      return;
    }
    if (!clientID || !selectedUnit || !selectedForm) {
      setGenerationContext(null);
      setDynamicValues({});
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await dispatch(
          getReturnSelectedYear(clientID, selectedUnit, selectedForm)
        );
        if (cancelled) return;
        if (res.success && res.data) {
          const { ctx, init, processDate: pd } = applyContextDefaults(res.data);
          setGenerationContext(ctx);
          setDynamicValues(init);
          if (pd) setProcessDate(pd);
        } else {
          setGenerationContext(null);
          setDynamicValues({});
          showSnackbar(res.message || "Could not load return options.", "error");
        }
      } catch {
        if (!cancelled) {
          setGenerationContext(null);
          setDynamicValues({});
          showSnackbar("Could not load return options.", "error");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientID, selectedUnit, selectedForm, selectedMode, dispatch]);

  const handleClientChange = async (e) => {
    const id = e.target.value;
    setClientID(id);
    setSelectedUnit("");
    setSelectedForm("");
    setSelectedAckFrequency("");
    setSelectedAckYear("");
    setReturnsRows([]);
    setProcessDate("");
    setGenerationContext(null);
    setDynamicValues({});
    resetMessages();
    resetFile();

    if (!id) {
      setClientName("");
      setUnitOptions([]);
      return;
    }

    const meta = clientOptions.find((c) => String(c.client_id) === String(id));
    setClientName(meta?.client_name || "");

    setLoading(true);
    try {
      const response = await dispatch(getUnits(id));
      const rows = Array.isArray(response.data) ? response.data : [];
      setUnitOptions(rows.filter((u) => String(u.client_id) === String(id)));
      if (!rows.length) {
        showSnackbar("No branches found for this organization.", "warning");
      }
    } catch {
      setUnitOptions([]);
      showSnackbar("Failed to load branches.", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleUnitChange = (e) => {
    const unitID = e.target.value;
    setSelectedUnit(unitID);
    setSelectedForm("");
    setSelectedAckFrequency("");
    setSelectedAckYear("");
    setProcessDate("");
    setGenerationContext(null);
    setDynamicValues({});
    resetMessages();
    resetFile();

    if (!unitID) {
      setReturnsRows([]);
      return;
    }

    if (selectedMode === MODES.ACK) {
      setReturnsRows([]);
      return;
    }

    (async () => {
      setLoading(true);
      try {
        const response = await dispatch(getReturnsFormsnew(unitID));
        if (response.success) {
          const rows = Array.isArray(response.data) ? response.data : [];
          setReturnsRows(rows);
          if (!rows.length) {
            const msg = "No returns configured for this branch.";
            setFailMessage(msg);
            showSnackbar(msg, "warning");
          }
        } else {
          setReturnsRows([]);
          const msg = response.message || "Failed to load returns.";
          setFailMessage(msg);
          showSnackbar(msg, "error");
        }
      } catch {
        setReturnsRows([]);
        setFailMessage("Failed to load returns.");
        showSnackbar("Failed to load returns.", "error");
      } finally {
        setLoading(false);
      }
    })();
  };

  const formOptions = Array.isArray(returnsRows)
    ? returnsRows.map((row) => ({
        value: row.form_name,
        label: row.frequency ? `${row.form_name} — ${row.frequency}` : row.form_name,
      }))
    : [];

  const handleFormSelectChange = (opt) => {
    setSelectedForm(opt?.value || "");
    setProcessDate("");
    setGenerationContext(null);
    setDynamicValues({});
    resetMessages();
    resetFile();
  };

  const resolveDateForApi = () => {
    const fromDynamic = pickDateFromDynamicValues(dynamicValues);
    if (fromDynamic) return String(fromDynamic).slice(0, 10);
    const def = generationContext?.default_generation_date;
    if (def) return String(def).slice(0, 10);
    if (processDate) {
      return hasYearOptions
        ? String(processDate).slice(0, 10)
        : toMonthFirstDay(processDate) || processDate;
    }
    return "";
  };

  const setSelectedFile = (selectedFile) => {
    setFile({
      raw: selectedFile,
      name: selectedFile.name,
      size: (selectedFile.size / (1024 * 1024)).toFixed(2) + " MB",
    });
  };

  const UPLOAD_FILE_PATTERN = /\.(pdf|xls|xlsx|doc|docx)$/i;
  const UPLOAD_FILE_ACCEPT = ".pdf,.xls,.xlsx,.doc,.docx";
  const UPLOAD_FILE_HINT = "Supports .xlsx, .xls, .pdf, .doc, .docx";

  const handleFileChange = (e) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;
    if (!selectedFile.name.match(UPLOAD_FILE_PATTERN)) {
      showSnackbar("Only PDF, Excel, or Word files are allowed", "error");
      return;
    }
    setSelectedFile(selectedFile);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const droppedFile = e.dataTransfer.files?.[0];
    if (!droppedFile) return;
    if (!droppedFile.name.match(UPLOAD_FILE_PATTERN)) {
      showSnackbar("Only PDF, Excel, or Word files are allowed", "error");
      return;
    }
    setSelectedFile(droppedFile);
  };

  const removeFile = () => resetFile();

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    resetMessages();

    if (!clientID) {
      showSnackbar("Please select an organization.", "error");
      return;
    }
    if (!selectedUnit) {
      showSnackbar("Please select a branch.", "error");
      return;
    }
    if (!selectedForm) {
      showSnackbar("Please select a return form.", "error");
      return;
    }

    if (hasYearOptions && !pickDateFromDynamicValues(dynamicValues)) {
      showSnackbar("Please select year.", "error");
      return;
    }

    for (const f of formInputs) {
      if (!f?.required) continue;
      const v = dynamicValues[f.name];
      if (v == null || String(v).trim() === "") {
        showSnackbar(`Please fill: ${f.label || f.name}`, "error");
        return;
      }
    }

    const dateForApi = resolveDateForApi();
    if (!dateForApi) {
      showSnackbar("Please select financial year or period date.", "error");
      return;
    }

    const return_inputs = buildReturnInputsPayload(dynamicValues, generationContext);

    setLoading(true);
    try {
      const user = JSON.parse(sessionStorage.getItem("user") || "{}");
      const formDataInput = {
        client_id: clientID,
        unit_id: selectedUnit,
        date: dateForApi,
        form_type: selectedForm,
        form_types: [selectedForm],
        client_name: clientName,
        mode :selectedMode,
        company_code: user?.company_code,
        format: selectedFormat,
        return_inputs,
      };

      const result = await dispatch(CreateGenerateFormInput(formDataInput));

      if (result.success) {
        const failuresSummary = result?.data?.failuresSummary;
        if (result.generatedFormId) {
          setStoredFormId(result.generatedFormId);
        }
        if (failuresSummary) {
          setSuccessMessage("Return generated with some failures.");
          setFailMessage(failuresSummary);
          showSnackbar("Return generated with some failures.", "warning");
        } else {
          setSuccessMessage("Return generated successfully.");
          showSnackbar("Return generated successfully.", "success");
        }
      } else {
        const reason =
          result?.message?.error ||
          result?.message?.message ||
          (typeof result?.message === "string" ? result.message : "Generation failed");
        setFailMessage(reason);
        showSnackbar(typeof reason === "string" ? reason : "Generation failed", "error");
      }
    } catch {
      setFailMessage("Generation failed. Please try again.");
      showSnackbar("Generation failed.", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleUploadSubmit = async (e) => {
    e.preventDefault();
    resetMessages();

    if (!clientID) {
      showSnackbar("Please select an organization.", "error");
      return;
    }
    if (!selectedUnit) {
      showSnackbar("Please select a branch.", "error");
      return;
    }
    if (!selectedForm) {
      showSnackbar("Please select a return form.", "error");
      return;
    }

    if (hasYearOptions && !pickDateFromDynamicValues(dynamicValues)) {
      showSnackbar("Please select year.", "error");
      return;
    }

    const dateForApi = resolveDateForApi();
    if (!dateForApi) {
      showSnackbar("Please select year.", "error");
      return;
    }

    if (!file?.raw) {
      showSnackbar("Please select a file to upload.", "error");
      return;
    }

    setLoading(true);
    try {
      const user = JSON.parse(sessionStorage.getItem("user") || "{}");
      const result = await dispatch(
        uploadReturnFile({
          file: file.raw,
          client_id: clientID,
          unit_id: selectedUnit,
          date: dateForApi,
          form_type: selectedForm,
          mode: selectedMode,
          company_code: user?.company_code,
        })
      );

      if (result.success) {
        if (result.generatedFormId) {
          setStoredFormId(result.generatedFormId);
        }
        const msg =
          typeof result.message === "string"
            ? result.message
            : "File uploaded successfully.";
        setSuccessMessage(msg);
        showSnackbar(msg, "success");
        resetFile();
      } else {
        const err =
          typeof result.message === "string" ? result.message : "Upload failed.";
        setFailMessage(err);
        showSnackbar(err, "error");
      }
    } catch {
      setFailMessage("Upload failed. Please try again.");
      showSnackbar("Upload failed.", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleAckUploadSubmit = async (e) => {
    e.preventDefault();
    resetMessages();

    if (!clientID) {
      showSnackbar("Please select an organization.", "error");
      return;
    }
    if (!selectedUnit) {
      showSnackbar("Please select a branch.", "error");
      return;
    }
    if (!selectedAckFrequency) {
      showSnackbar("Please select return form frequency.", "error");
      return;
    }
    if (!selectedAckYear) {
      showSnackbar("Please select year.", "error");
      return;
    }
    if (!file?.raw) {
      showSnackbar("Please select a file to upload.", "error");
      return;
    }

    const dateForApi = `${selectedAckYear}-04-01`;

    setLoading(true);
    try {
      const user = JSON.parse(sessionStorage.getItem("user") || "{}");
      const result = await dispatch(
        uploadReturnFile({
          file: file.raw,
          client_id: clientID,
          unit_id: selectedUnit,
          date: dateForApi,
          form_type: `Acknowledgement_${selectedAckFrequency}`,
          mode: selectedMode,
          company_code: user?.company_code,
        })
      );

      if (result.success) {
        if (result.generatedFormId) {
          setStoredFormId(result.generatedFormId);
        }
        const msg =
          typeof result.message === "string"
            ? result.message
            : "File uploaded successfully.";
        setSuccessMessage(msg);
        showSnackbar(msg, "success");
        resetFile();
      } else {
        const err =
          typeof result.message === "string" ? result.message : "Upload failed.";
        setFailMessage(err);
        showSnackbar(err, "error");
      }
    } catch {
      setFailMessage("Upload failed. Please try again.");
      showSnackbar("Upload failed.", "error");
    } finally {
      setLoading(false);
    }
  };

  

  const handleDownloadStored = async () => {
    if (!storedFormId) return;
    setLoading(true);
    try {
      const response = await dispatch(Generated_forms_downloadDataInput(storedFormId));
      if (response?.success !== false) {
        showSnackbar("Download started.", "success");
      } else {
        showSnackbar("Download failed.", "error");
      }
    } catch {
      showSnackbar("Download failed.", "error");
    } finally {
      setLoading(false);
    }
  };

  const renderStoredDownload = () =>
    storedFormId ? (
      <div className="mt-3">
        <button
          type="button"
          className="btn return-download-btn w-100"
          onClick={handleDownloadStored}
        >
          Download stored file
        </button>
      </div>
    ) : null;

  const renderModeTabs = () => (
    <div className="return-mode-tabs">
      {Object.values(MODES).map((mode) => (
        <label key={mode} className="return-mode-tab">
          <input
            type="radio"
            name="returnModeSelector"
            value={mode}
            checked={selectedMode === mode}
            onChange={handleModeChange}
          />
          <span className="return-mode-tab-content">
            <span className="return-mode-tab-title">
              {MODE_META[mode]?.title || mode}
            </span>
            <span className="return-mode-tab-desc">
              {MODE_META[mode]?.desc || ""}
            </span>
          </span>
        </label>
      ))}
    </div>
  );

  const renderOrgBranchFields = () => (
    <div className="return-form-grid two-col">
      <div className="return-field-group">
        <label className="form-label">Organization</label>
        <select className="form-select" value={clientID} onChange={handleClientChange}>
          <option value="">Select organization</option>
          {clientOptions.map((c) => (
            <option key={c.client_id} value={c.client_id}>
              {orgOptionLabel(c)}
            </option>
          ))}
        </select>
      </div>
      <div className="return-field-group">
        <label className="form-label">Branch</label>
        <select
          className="form-select"
          value={selectedUnit}
          onChange={handleUnitChange}
          disabled={!clientID}
        >
          <option value="">Select branch</option>
          {unitOptions.map((unit) => (
            <option key={unit.unit_id} value={unit.unit_id}>
              {branchOptionLabel(unit)}
            </option>
          ))}
        </select>
      </div>
    </div>
  );

  const renderYearSelect = () => {
    if (!selectedForm) return null;
    if (hasYearOptions) {
      const periodFieldName = getPeriodFieldName(generationContext);
      const periodValue =
        dynamicValues[periodFieldName] ??
        dynamicValues.financial_year_start ??
        "";
      return (
        <div className="return-field-group">
          <label className="form-label">
            {yearTypeLabel ? yearTypeLabel : "Year"}{" "}
            <span className="text-danger">*</span>
          </label>
          <select
            className="form-select"
            value={periodValue}
            onChange={(e) => {
              const v = e.target.value;
              const periodDate = v ? String(v).slice(0, 10) : "";
              setDynamicValues((prev) => {
                const next = { ...prev, [periodFieldName]: periodDate };
                if (periodFieldName !== "financial_year_start") {
                  next.financial_year_start = periodDate;
                }
                return next;
              });
              if (periodDate) setProcessDate(periodDate);
            }}
          >
            <option value="">Select year</option>
            {generationContext.year_options.map((opt) => (
              <option key={String(opt.value)} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      );
    }

    return (
      <div className="return-field-group">
        <label className="form-label">
          Year <span className="text-danger">*</span>
        </label>
        <input
          className="form-control"
          type="date"
          value={processDate}
          onChange={(ev) => setProcessDate(ev.target.value)}
        />
      </div>
    );
  };

  const renderFileUpload = (acceptHint) => (
    <div className="return-field-group mt-2">
      <label className="form-label">Upload file</label>
      <div className="row">
        <div className="col-md-12 upload-box-main1">
          <div
            className={`upload-box d-flex align-items-center justify-content-between ${
              dragActive ? "drag-active" : ""
            }`}
            onDragOver={(ev) => {
              ev.preventDefault();
              setDragActive(true);
            }}
            onDragLeave={() => setDragActive(false)}
            onDrop={handleDrop}
          >
            <div className="d-flex align-items-center">
              <div className="upload-icon me-2">
                <img src={file_upload_icon} alt="" />
              </div>
              <div>
                <p className="p-0 m-0">
                  {dragActive ? "Drop file here" : "Select a file or drag and drop here"}
                </p>
                <small className="text-muted">{acceptHint}</small>
              </div>
            </div>
            <a onClick={() => document.getElementById("returnFileInput").click()}>
              SELECT FILE
            </a>
            <input
              type="file"
              id="returnFileInput"
              className="d-none"
              onChange={handleFileChange}
              accept={UPLOAD_FILE_ACCEPT}
            />
          </div>
        </div>
        {file && (
          <div className="col-md-12 uploaded-box-main1 p-0 m-0">
            <div className="uploaded-file d-flex align-items-center justify-content-between">
              <div className="d-flex align-items-center">
                <img src={file_uploaded_icon} alt="" />
                <div>
                  <p className="mb-0 uploaded-file-name">{file.name}</p>
                  <small className="text-muted uploaded-file-size">{file.size}</small>
                </div>
              </div>
              <a className="close-btn p-0" onClick={removeFile}>
                <img src={into} alt="" />
              </a>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const renderSharedSelectors = () => (
    <>
      <p className="return-section-title">Return details</p>
      {renderOrgBranchFields()}

      <div className="return-field-group mt-2">
        <label className="form-label">Return form</label>
        <Select
          options={formOptions}
          value={formOptions.find((o) => o.value === selectedForm) || null}
          onChange={handleFormSelectChange}
          placeholder="Select return"
          isSearchable
          isDisabled={!selectedUnit}
          styles={selectCustomStyles}
        />
      </div>

      {renderYearSelect()}
    </>
  );

  const renderAcknowledgementSelectors = () => (
    <>
      <p className="return-section-title">Acknowledgement details</p>
      {renderOrgBranchFields()}

      <div className="return-form-grid two-col mt-2">
        <div className="return-field-group">
          <label className="form-label">
            Return form <span className="text-danger">*</span>
          </label>
          <select
            className="form-select"
            value={selectedAckFrequency}
            onChange={(e) => {
              setSelectedAckFrequency(e.target.value);
              resetMessages();
              resetFile();
              resetStoredForm();
            }}
            disabled={!selectedUnit}
          >
            <option value="">Select frequency</option>
            {ACK_FREQUENCY_OPTIONS.map((frequency) => (
              <option key={frequency} value={frequency}>
                {frequency}
              </option>
            ))}
          </select>
        </div>

        <div className="return-field-group">
          <label className="form-label">
            Year <span className="text-danger">*</span>
          </label>
          <select
            className="form-select"
            value={selectedAckYear}
            onChange={(e) => setSelectedAckYear(e.target.value)}
            disabled={!selectedAckFrequency}
          >
            <option value="">Select year</option>
            {Array.from({ length: 14 }).map((_, index) => {
              const year = String(new Date().getFullYear() + 1 - index);
              return (
                <option key={year} value={year}>
                  {year}
                </option>
              );
            })}
          </select>
        </div>
      </div>
    </>
  );

  return (
    <div className="container-fluid return-generate-form">
      <div className="card generate-form-card">
        <div className="card-body">
          <div className="returns-card-header">
            <h1>Returns</h1>
            <p className="returns-subtitle">
              Generate a return, override an existing generated file, or upload an
              acknowledgement document.
            </p>
          </div>

          {renderModeTabs()}

          <div className="return-form-panel">
            {selectedMode === MODES.GENERATE && (
              <form onSubmit={handleFormSubmit}>
                {renderSharedSelectors()}

                {!hasYearOptions && (
                  <div className="return-field-group mt-2">
                    <label className="form-label">Period date</label>
                    <input
                      className="form-control"
                      type="date"
                      value={processDate}
                      onChange={(ev) => setProcessDate(ev.target.value)}
                      disabled={!selectedForm}
                    />
                  </div>
                )}

                {selectedForm && formInputs.length > 0 && (
                  <div className="return-form-section">
                    <div className="return-form-section-title">Form inputs</div>
                    {formInputs.map((field) => (
                      <div key={field.name} className="return-field-group">
                        <label className="form-label">
                          {field.label || field.name}
                          {field.required && <span className="text-danger"> *</span>}
                        </label>
                        {renderUserInput(field)}
                      </div>
                    ))}
                  </div>
                )}

                <div className="return-field-group mt-2">
                  <label className="form-label">Output format</label>
                  <select
                    className="form-select"
                    value={selectedFormat}
                    onChange={(ev) => setSelectedFormat(ev.target.value)}
                  >
                    <option value="excel">Excel (.xlsx)</option>
                    <option value="pdf">PDF</option>
                    <option value="docx">Word (.docx)</option>
                    <option value="doc">Word legacy (.doc)</option>
                    <option value="both">Excel + PDF (ZIP)</option>
                    <option value="all">Excel + PDF + Word (ZIP)</option>
                  </select>
                </div>

                <button
                  type="submit"
                  className="btn btn-primary return-submit-btn w-100"
                  disabled={!selectedForm}
                >
                  Generate return
                </button>
              </form>
            )}

            {selectedMode === MODES.OVERRIDE && (
              <form onSubmit={handleUploadSubmit}>
                {renderSharedSelectors()}
                {renderFileUpload(UPLOAD_FILE_HINT)}
                <button
                  type="submit"
                  className="btn btn-primary return-submit-btn w-100"
                  disabled={!selectedForm || !file}
                >
                  Upload override
                </button>
              </form>
            )}

            {selectedMode === MODES.ACK && (
              <form onSubmit={handleAckUploadSubmit}>
                {renderAcknowledgementSelectors()}
                {renderFileUpload(UPLOAD_FILE_HINT)}
                <button
                  type="submit"
                  className="btn btn-primary return-submit-btn w-100"
                  disabled={!selectedAckFrequency || !selectedAckYear || !file}
                >
                  Upload acknowledgement
                </button>
              </form>
            )}
          </div>

          {successMessage && (
            <div className="alert alert-success mt-3">{successMessage}</div>
          )}
          {failMessage && <div className="alert alert-danger mt-3">{failMessage}</div>}
          {renderStoredDownload()}
        </div>
      </div>
    </div>
  );
}

function ReturnGenerateForm(props) {
  try {
    const role = JSON.parse(sessionStorage.getItem("user") || "{}")?.base_role;
    if (role === "Client") {
      return <ClientReturnsPortal {...props} />;
    }
  } catch {
    /* keep admin UI */
  }
  return <AdminReturnGenerateForm {...props} />;
}

export default ReturnGenerateForm;
